import Repository, {
  ExtendedAxiosRequestConfig,
} from "@shared/services/api/abstracts/Repository";
import { Identifier, UrlRouteMap } from "@shared/services/api/types";
import * as RepositoryUrlUtils from "@shared/services/api/utils/RepositoryUrlUtils";
import { TaskedEndpointResponse } from "@shared/services/loaders/api";
import { taskLoader } from "@shared/services/loaders/task-loader";
import { AxiosRequestConfig } from "axios";

import { getPlatform } from "@shared/utils/get-platform";
import {
  VenueBasedTicketBasketHoldRequest,
  VenueBasedTicketBasketPurchaseRequest,
  VenueBasedTicketBasketPurchaseResponse,
  VenueBasedTicketBasketSerializer,
  VenueBasedTicketBasketSquareTerminalStatusResponse,
} from "../../../data/types/basket";

export default class VenueBasedTicketBasketRepository extends Repository<VenueBasedTicketBasketSerializer> {
  public readonly repositoryName = "VenueBasedTicketBasketRepository";

  public readonly endpoint =
    RepositoryUrlUtils.venueBasedUrl("tickets/baskets/");

  public get urlMap(): UrlRouteMap {
    return {
      ...this.defaultUrlMap,
      expire: `{id}/expire`,
      purchase: `{id}/purchase`,
      reset: `{id}/reset`,
      getTerminalStatus: `{id}/terminal-status`,
      hold: `{id}/hold`,
      deliverTickets: `{id}/deliver-tickets/`,
    };
  }

  public async create(
    attributes: Pick<
      VenueBasedTicketBasketSerializer,
      "item_groups" | "purchase_source_platform"
    >,
    config: AxiosRequestConfig = {}
  ): Promise<VenueBasedTicketBasketSerializer> {
    return super.create(attributes, config);
  }

  public async update(
    id: Identifier,
    attributes: Partial<
      Pick<
        VenueBasedTicketBasketSerializer,
        "item_groups" | "purchase_source_platform" | "payment_type"
      >
    >,
    config: AxiosRequestConfig = {}
  ): Promise<VenueBasedTicketBasketSerializer> {
    return super.update(id, attributes, { showToastOnError: true, ...config });
  }

  public async expire(
    id: number,
    config: AxiosRequestConfig = {}
  ): Promise<void> {
    await this.request(this.buildUrl("expire", { id }), {
      method: "post",
      showToastOnError: getPlatform() !== "MOBILE",
      ...config,
    });
  }

  public async purchase(
    id: number,
    attributes: VenueBasedTicketBasketPurchaseRequest,
    config: ExtendedAxiosRequestConfig = {}
  ): Promise<VenueBasedTicketBasketPurchaseResponse> {
    const { data } = await this.request<VenueBasedTicketBasketPurchaseResponse>(
      this.buildUrl("purchase", { id }),
      {
        method: "post",
        data: attributes,
        ...config,
      }
    );

    return data;
  }

  public async reset(
    id: number,
    config: ExtendedAxiosRequestConfig = {}
  ): Promise<{ id: number; expiry_date: string }> {
    const { data } = await this.request<{ id: number; expiry_date: string }>(
      this.buildUrl("reset", { id }),
      {
        method: "post",
        ...config,
      }
    );

    return data;
  }

  public async getTerminalStatus(
    id: number
  ): Promise<VenueBasedTicketBasketSquareTerminalStatusResponse> {
    const { data } =
      await this.request<VenueBasedTicketBasketSquareTerminalStatusResponse>(
        this.buildUrl("getTerminalStatus", { id })
      );
    return data;
  }

  // returns an empty object
  public async hold(
    attributes: VenueBasedTicketBasketHoldRequest,
    config: AxiosRequestConfig = {}
  ): Promise<unknown> {
    const { data } = await this.request<TaskedEndpointResponse>(
      this.buildUrl("hold", { id: attributes.id }),
      {
        method: "post",
        data: attributes,
        ...config,
      }
    );

    if ("job_id" in data) {
      return taskLoader(data.job_id);
    }

    return data;
  }

  public async deliverTickets(
    id: number,
    config: ExtendedAxiosRequestConfig = {}
  ): Promise<VenueBasedTicketBasketPurchaseResponse> {
    const { data } = await this.request<VenueBasedTicketBasketPurchaseResponse>(
      this.buildUrl("deliverTickets", { id }),
      {
        method: "post",
        ...config,
      }
    );

    return data;
  }

  // TODO cancelSquarePurchase
}
